/// <reference types="./assoc.d.mts" />
import * as $list from "../../gleam_stdlib/gleam/list.mjs";
import * as $option from "../../gleam_stdlib/gleam/option.mjs";
import { None, Some } from "../../gleam_stdlib/gleam/option.mjs";
import * as $pair from "../../gleam_stdlib/gleam/pair.mjs";
import { Ok, Error, toList, Empty as $Empty, prepend as listPrepend, isEqual } from "../gleam.mjs";

export function new$() {
  return $list.new$();
}

export function push(assoc, key, value) {
  return $list.append(assoc, toList([[key, value]]));
}

export function append(assoc, assoc2) {
  return $list.append(assoc, assoc2);
}

export function flatten(assocs) {
  return $list.flatten(assocs);
}

export function fold(assocs, initial_value, folder) {
  return $list.fold(assocs, initial_value, folder);
}

export function get(loop$assoc, loop$key) {
  while (true) {
    let assoc = loop$assoc;
    let key = loop$key;
    if (assoc instanceof $Empty) {
      return new Error(undefined);
    } else {
      let old = assoc.head[0];
      if (isEqual(key, old)) {
        let value = assoc.head[1];
        return new Ok(value);
      } else {
        let rest = assoc.tail;
        loop$assoc = rest;
        loop$key = key;
      }
    }
  }
}

export function optional_get(assoc, key) {
  let _pipe = get(assoc, key);
  return $option.from_result(_pipe);
}

function do_all(loop$assoc, loop$key, loop$res) {
  while (true) {
    let assoc = loop$assoc;
    let key = loop$key;
    let res = loop$res;
    if (assoc instanceof $Empty) {
      return $list.reverse(res);
    } else {
      let old = assoc.head[0];
      if (isEqual(key, old)) {
        let rest = assoc.tail;
        let value = assoc.head[1];
        loop$assoc = rest;
        loop$key = key;
        loop$res = listPrepend(value, res);
      } else {
        let rest = assoc.tail;
        loop$assoc = rest;
        loop$key = key;
        loop$res = res;
      }
    }
  }
}

export function all(assoc, key) {
  return do_all(assoc, key, toList([]));
}

export function map(assoc, mapper) {
  return $list.map(assoc, mapper);
}

export function map_keys(assoc, mapper) {
  return $list.map(
    assoc,
    (_capture) => { return $pair.map_first(_capture, mapper); },
  );
}

export function map_values(assoc, mapper) {
  return $list.map(
    assoc,
    (_capture) => { return $pair.map_second(_capture, mapper); },
  );
}

function reverse_and_prepend(loop$prefix, loop$suffix) {
  while (true) {
    let prefix = loop$prefix;
    let suffix = loop$suffix;
    if (prefix instanceof $Empty) {
      return suffix;
    } else {
      let first = prefix.head;
      let rest = prefix.tail;
      loop$prefix = rest;
      loop$suffix = listPrepend(first, suffix);
    }
  }
}

function do_set(loop$assoc, loop$key, loop$value, loop$res) {
  while (true) {
    let assoc = loop$assoc;
    let key = loop$key;
    let value = loop$value;
    let res = loop$res;
    if (assoc instanceof $Empty) {
      return $list.reverse(listPrepend([key, value], res));
    } else {
      let item = assoc.head;
      if (!isEqual(key, item[0])) {
        let rest = assoc.tail;
        loop$assoc = rest;
        loop$key = key;
        loop$value = value;
        loop$res = listPrepend(item, res);
      } else {
        let rest = assoc.tail;
        return reverse_and_prepend(res, listPrepend([key, value], rest));
      }
    }
  }
}

export function set(assoc, key, value) {
  return do_set(assoc, key, value, toList([]));
}

export function optional_set(assoc, key, value) {
  if (value instanceof Some) {
    let value$1 = value[0];
    return set(assoc, key, value$1);
  } else {
    return assoc;
  }
}

function do_upsert(loop$assoc, loop$key, loop$mapper, loop$res) {
  while (true) {
    let assoc = loop$assoc;
    let key = loop$key;
    let mapper = loop$mapper;
    let res = loop$res;
    if (assoc instanceof $Empty) {
      return $list.reverse(listPrepend([key, mapper(new None())], res));
    } else {
      let item = assoc.head;
      if (isEqual(item[0], key)) {
        let rest = assoc.tail;
        return reverse_and_prepend(
          res,
          listPrepend([key, mapper(new Some(item[1]))], rest),
        );
      } else {
        let item$1 = assoc.head;
        let rest = assoc.tail;
        loop$assoc = rest;
        loop$key = key;
        loop$mapper = mapper;
        loop$res = listPrepend(item$1, res);
      }
    }
  }
}

export function upsert(assoc, key, mapper) {
  return do_upsert(assoc, key, mapper, toList([]));
}

function do_remove(loop$assoc, loop$key, loop$res) {
  while (true) {
    let assoc = loop$assoc;
    let key = loop$key;
    let res = loop$res;
    if (assoc instanceof $Empty) {
      return $list.reverse(res);
    } else {
      let old = assoc.head[0];
      if (isEqual(old, key)) {
        let rest = assoc.tail;
        return reverse_and_prepend(res, rest);
      } else {
        let item = assoc.head;
        let rest = assoc.tail;
        loop$assoc = rest;
        loop$key = key;
        loop$res = listPrepend(item, res);
      }
    }
  }
}

export function remove(assoc, key) {
  return do_remove(assoc, key, toList([]));
}

function do_remove_all(loop$assoc, loop$key, loop$res) {
  while (true) {
    let assoc = loop$assoc;
    let key = loop$key;
    let res = loop$res;
    if (assoc instanceof $Empty) {
      return $list.reverse(res);
    } else {
      let old = assoc.head[0];
      if (isEqual(old, key)) {
        let rest = assoc.tail;
        loop$assoc = rest;
        loop$key = key;
        loop$res = res;
      } else {
        let item = assoc.head;
        let rest = assoc.tail;
        return do_remove(rest, key, listPrepend(item, res));
      }
    }
  }
}

export function remove_all(assoc, key) {
  return do_remove_all(assoc, key, toList([]));
}
